import React from 'react'

interface LoadWhenVisibleOnlyProps {
  children: React.ReactNode
  intersectionThreshold?: number
  rootMarginPx?: number
  Placeholder?: () => JSX.Element
}

export const LoadWhenInView = ({
  children,
  Placeholder,
  intersectionThreshold = 0.1,
  rootMarginPx = 100
}: LoadWhenVisibleOnlyProps) => {
  const options: IntersectionObserverInit = React.useMemo(
    () => ({
      root: null,
      rootMargin: `${rootMarginPx}px`,
      threshold: intersectionThreshold
    }),
    [rootMarginPx, intersectionThreshold]
  )

  const [isVisible, targetRef] = useIsVisible(options)

  if (Placeholder) {
    return <div ref={targetRef}>{isVisible ? children : <Placeholder />}</div>
  }

  return <div ref={targetRef}>{isVisible && children}</div>
}

const useIsVisible = (
  options: IntersectionObserverInit
): [boolean, React.MutableRefObject<HTMLDivElement | null>] => {
  const [isVisible, setIsVisible] = React.useState(false)
  const targetRef = React.useRef<HTMLDivElement | null>(null)

  const observerCallback: IntersectionObserverCallback = React.useCallback(
    entries => {
      const [targetEntry] = entries
      setIsVisible(prev => {
        if (prev) {
          return prev
        }

        return targetEntry.isIntersecting
      })
    },
    []
  )

  React.useEffect(() => {
    const target = targetRef.current
    const observer = new IntersectionObserver(observerCallback, options)

    if (target) {
      observer.observe(target)
    }

    return () => {
      observer.disconnect()
    }
  }, [observerCallback, options])

  return [isVisible, targetRef]
}
